/**
 * "This looks wrong — are you sure?" (Plans D3 and D4.)
 *
 * The one place the system asks staff to confirm something it will then record.
 * Shared by the roster and the attendance form because the two questions are the
 * same shape, and because a confirmation that looks different depending on which
 * screen raised it is one staff read twice and trust less.
 *
 * **It names what is wrong.** A clash renders the other sessions — what they are,
 * when they are, and whether the volunteer is merely down for them or already
 * recorded at them. "There is a conflict" would leave staff to go and look, and
 * a warning you have to investigate is one you learn to dismiss.
 *
 * The details come from the error body, never from parsing its message: the
 * backend owns the wording, this owns the layout.
 */

import { API_ERROR_CODE } from '@parinaam-vms/shared';
import type { OverlapDetails, ScheduleConflict } from '@parinaam-vms/shared';
import type { ApiRequestError } from '../../services/api/client';
import { formatDate } from '../../utils/format';
import { Button } from './Button';
import styles from './ConfirmWarning.module.css';

export interface ConfirmWarningProps {
  /** Every question the API has asked about this attempt, oldest first. */
  questions: ApiRequestError[];
  /** What saying yes will do, in the user's words: "Record it anyway". */
  confirmLabel: string;
  pending?: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}

export function ConfirmWarning({
  questions,
  confirmLabel,
  pending = false,
  onConfirm,
  onCancel,
}: ConfirmWarningProps) {
  if (questions.length === 0) return null;

  return (
    <div className={styles.warning} role="alert">
      {questions.map((question) => (
        <div key={question.code} className={styles.question}>
          <p className={styles.message}>{question.message}</p>
          <ConflictList error={question} />
        </div>
      ))}

      <p className={styles.note}>
        {questions.length > 1
          ? 'Both exceptions will be noted in the audit trail against your name.'
          : 'The exception will be noted in the audit trail against your name.'}
      </p>

      <div className={styles.actions}>
        <Button variant="primary" loading={pending} onClick={onConfirm}>
          {confirmLabel}
        </Button>
        <Button onClick={onCancel} disabled={pending}>
          Go back
        </Button>
      </div>
    </div>
  );
}

/** The clashing sessions, when the question is an overlap. Nothing otherwise. */
function ConflictList({ error }: { error: ApiRequestError }) {
  if (error.code !== API_ERROR_CODE.OVERLAP_DETECTED) return null;

  const conflicts = (error.details as OverlapDetails | undefined)?.conflicts ?? [];
  if (conflicts.length === 0) return null;

  return (
    <ul className={styles.conflicts}>
      {conflicts.map((conflict) => (
        <li key={`${conflict.source}-${conflict.activity_id}`}>
          <strong>{conflict.activity_name}</strong>
          <span className={styles.detail}>
            {' '}
            · {hhmm(conflict.start_time)}–{hhmm(conflict.end_time)} on{' '}
            {formatDate(conflict.conflict_date)} · {conflict.program_name}
          </span>
          <span className={styles.detail}> — {commitment(conflict)}</span>
        </li>
      ))}
    </ul>
  );
}

/**
 * What kind of commitment this is, because the two mean different things.
 *
 * A roster place is a plan and can still be changed; recorded attendance is a
 * claim about where somebody actually was, so one of the two records is wrong.
 */
function commitment(conflict: ScheduleConflict): string {
  return conflict.source === 'ATTENDANCE'
    ? 'hours already recorded here'
    : 'already on this roster';
}

/** The API sends `HH:MM:SS`; nobody needs the seconds. */
function hhmm(time: string): string {
  return time.slice(0, 5);
}
